import React, { useEffect, useState } from 'react';
import { Users, MapPin, CreditCard, Smartphone, ArrowRight, Shield, TrendingUp } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useBrand } from '../../contexts/BrandContext';
import { PERMISSIONS } from '../../utils/permissions';
import { settingsApi } from '../../services/api';

interface LandingPageProps {
  onSelectPortal: (portal: 'admin' | 'member') => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onSelectPortal }) => {
  const { hasPermission } = usePermissions();
  const { brand } = useBrand();
  const [adminContact, setAdminContact] = useState<string | null>(null);


  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await settingsApi.get();
        if (mounted && res.success && res.data) {
          setAdminContact(res.data.settings?.adminContact || res.data.adminContact || null);
        }
      } catch {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none';
    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
    if (nextElement) {
      nextElement.style.display = 'block';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 dark:from-dark-bg-primary dark:to-dark-bg-secondary">
      {/* Header */}
      <header className="bg-white dark:bg-dark-bg-secondary shadow-sm border-b dark:border-dark-border-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img 
                src={brand.brandLogo} 
                alt={brand.brandName}
                className="h-6 w-6 sm:h-8 sm:w-8 object-contain"
                onError={handleImageError}
              />
              <div 
                className="p-1 sm:p-2 rounded-lg" 
                style={{ display: 'none', backgroundColor: brand.brandColors.primary }}
              >
                <Users className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-dark-text-primary">
                  {brand.brandName.toUpperCase()}
                </h1>
                <p className="text-xs text-gray-500 hidden sm:block">{brand.brandTagline}</p>
              </div>
            </div>
            <div className="text-xs sm:text-sm text-gray-600 hidden md:block">
              🇬🇭 Built for Ghanaian Families
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-dark-text-primary mb-6">
            Manage Your Family
            <span className="text-blue-600 block">The Modern Way</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-dark-text-secondary mb-12 max-w-2xl mx-auto">
            {brand.brandDescription}
          </p>

          {/* Portal Selection Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Admin Portal Card */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-xl p-8 border dark:border-dark-border-primary hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">Admin Portal</h3>
              <p className="text-gray-600 dark:text-dark-text-secondary mb-6">
                Complete management dashboard for administrators. Manage members, track finances, 
                organize events, and generate comprehensive reports.
              </p>
              <ul className="text-left space-y-2 mb-8">
                <li className="flex items-center text-gray-700 dark:text-dark-text-secondary">
                  <Users className="h-4 w-4 text-green-600 mr-2" />
                  Member Management
                </li>
                <li className="flex items-center text-gray-700 dark:text-dark-text-secondary">
                  <CreditCard className="h-4 w-4 text-green-600 mr-2" />
                  Financial Tracking
                </li>
                <li className="flex items-center text-gray-700 dark:text-dark-text-secondary">
                  <MapPin className="h-4 w-4 text-green-600 mr-2" />
                  Multi-Town Support
                </li>
                <li className="flex items-center text-gray-700 dark:text-dark-text-secondary">
                  <TrendingUp className="h-4 w-4 text-green-600 mr-2" />
                  Excel Reports
                </li>
              </ul>
              <button
                onClick={() => onSelectPortal('admin')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
              >
                <span>Access Admin Portal</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {/* Member Portal Card */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-xl p-8 border dark:border-dark-border-primary hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Smartphone className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">Member Portal</h3>
              <p className="text-gray-600 dark:text-dark-text-secondary mb-6">
                Mobile-optimized portal for family members. View your contributions, 
                make payments via Mobile Money, and stay updated on events.
              </p>
              <ul className="text-left space-y-2 mb-8">
                <li className="flex items-center text-gray-700 dark:text-dark-text-secondary">
                  <CreditCard className="h-4 w-4 text-green-600 mr-2" />
                  View Contributions
                </li>
                <li className="flex items-center text-gray-700 dark:text-dark-text-secondary">
                  <Smartphone className="h-4 w-4 text-green-600 mr-2" />
                  Mobile Money Payments
                </li>
                <li className="flex items-center text-gray-700 dark:text-dark-text-secondary">
                  <Users className="h-4 w-4 text-green-600 mr-2" />
                  Event Registration
                </li>
                <li className="flex items-center text-gray-700 dark:text-dark-text-secondary">
                  <MapPin className="h-4 w-4 text-green-600 mr-2" />
                  Town Information
                </li>
              </ul>
              <button
                onClick={() => onSelectPortal('member')}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
              >
                <span>Access Member Portal</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white dark:bg-dark-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
              Built for Ghanaian Families
            </h2>
            <p className="text-xl text-gray-600 dark:text-dark-text-secondary max-w-2xl mx-auto">
              Every feature is designed with Ghana's unique family structure and mobile-first culture in mind.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-2">Mobile Money Integration</h3>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Seamless integration with MTN MoMo, Vodafone Cash, and AirtelTigo Money for easy payments.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-2">Multi-Town Management</h3>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Manage multiple family branches from a single dashboard with town-specific data tracking.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-2">Professional Reports</h3>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Generate comprehensive Excel reports for financial tracking, member management, and events.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-4">
              <img 
                src={brand.brandLogo} 
                alt={brand.brandName}
                className="h-6 w-6 sm:h-8 sm:w-8 object-contain"
                onError={handleImageError}
              />
              <div 
                className="p-1 sm:p-2 rounded-lg" 
                style={{ display: 'none', backgroundColor: brand.brandColors.primary }}
              >
                <Users className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
              </div>
              <span className="text-lg sm:text-xl font-bold">{brand.brandName.toUpperCase()}</span>
            </div>
            <p className="text-gray-400 mb-4 text-sm sm:text-base">
              {brand.brandTagline}
            </p>
            <div className="space-y-2">
              <p className="text-xs sm:text-sm text-gray-500">
                © {new Date().getFullYear()} {brand.organizationName}. Built with <span aria-hidden>❤️</span> for Ghanaian families.
              </p>
              <p className="text-xs sm:text-sm text-gray-500">
                🇬🇭 Empowering families with modern membership management
              </p>
              {hasPermission(PERMISSIONS.MANAGE_USERS) && adminContact && (
                <p className="text-xs sm:text-sm text-gray-500">Contact: {adminContact}</p>
              )}
              {process.env.NODE_ENV === 'development' && (
                <p className="text-xs text-purple-400 mt-2">Development Mode - Developer Portal Available</p>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};